import { useQuery } from "@apollo/client";
import { Col, Empty, Form, Row, Table, Tag, Typography } from "antd";
import Search from "antd/lib/input/Search";
import { PaginationConfig } from "antd/lib/pagination";
import { format, subDays } from "date-fns";
import React, { useState } from "react";
import Error from "../../components/apollo_error";
import DateRange from "../../components/date_range";
import LoadingSpinner from "../../components/loading_indicator";
import MultiSelect from "../../components/multi_select";
import {
  GET_STOCK_HISTORIES,
  getStockHistoryVariables,
  get_total_stock_history_count,
} from "../../graphql/inventory";
import withClinic from "../../hooks/with_clinic";
import { ApolloResult, Clinic } from "../../store";

interface P {
  currentClinic?: Clinic;
}

interface StockHistory {
  id: string;
  qty: number;
  closing_qty: number;
  stock_date: string;
  transaction_type: string;
  description: string;
  ref_id: string;
  ref_type: string;
  ref_detail_id: string;
  stock_id: string;
  created_at: string;
  stock: {
    id: string;
    name: string;
    product: {
      id: string;
      name: string;
    };
  };
}

const InventoryHistoryPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>(
    []
  );
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  });

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const { variables } = getStockHistoryVariables({
    clinic_id: clinicId,
    from_date: ranges.startDate.toISOString(),
    to_date: ranges.endDate.toISOString(),
    take,
    skip,
    searchText: searchText,
    refTypes:transactionTypeFilter
  });
  const { loading, data, error } = useQuery<
    ApolloResult<"stockHistories", StockHistory[]>
  >(GET_STOCK_HISTORIES, { variables });

  const totalCount = useQuery<
    ApolloResult<"aggregateStockHistory", { _count: { id: number } }>
  >(get_total_stock_history_count, { variables: { where: variables.where } });

  if (loading || totalCount.loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (totalCount.error) return <Error error={totalCount.error} />;
  if (!data) return <Empty />;

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  const getRefTypeColor = (type: string) => {
    switch (type) {
      case "sale":
        return "red";
      case "product":
        return "blue";
      case "service":
        return "green";
      case "adjustment":
        return "orange";
      case "opening":
        return "purple";
      default:
        return "default";
    }
  };

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "stock_date",
      key: "stock_date",
      editable: false,
      width: 150,
      ellipsis: true,
      sorter: (a: StockHistory, b: StockHistory) =>
        new Date(a.stock_date).getTime() - new Date(b.stock_date).getTime(),
      render: (v: string, r: StockHistory) => {
        return format(new Date(r.stock_date), "dd MMM, yyyy h:mm a");
      },
    },
    {
      title: "Stock Name",
      dataIndex: ["stock", "name"],
      key: "stock_name",
      editable: false,
      width: 200,
      ellipsis: true,
      sorter: (a: StockHistory, b: StockHistory) =>
        a.stock.name.toLowerCase().localeCompare(b.stock.name.toLowerCase()),
    },
    {
      title: "Stock",
      dataIndex: "qty",
      key: "qty",
      editable: false,
      width: 120,
      sorter: (a: StockHistory, b: StockHistory) => a.qty - b.qty,
      render: (v: number, r: StockHistory) => {
        const color = r.qty > 0 ? "green" : "red";
        return (
          <span style={{ color }}>
            {r.qty > 0 ? "+" : ""}
            {r.qty}
          </span>
        );
      },
    },
    {
      title: "Stock After",
      dataIndex: "closing_qty",
      key: "closing_qty",
      width: 130,
      sorter: (a: StockHistory, b: StockHistory) =>
        a.closing_qty - b.closing_qty,
      editable: false,
      render: (v: number, r: StockHistory) => {
        return r.closing_qty;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      editable: false,
      width: 300,
      render: (v: string, r: StockHistory) => {
        return (
          <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }}>
            {r.description}
          </Typography.Paragraph>
        );
      },
    },
    {
      title: "Event",
      dataIndex: "ref _type",
      key: "ref_type",
      width: 130,
      ellipsis: true,
      sorter: (a: StockHistory, b: StockHistory) =>
        a.ref_type.localeCompare(b.ref_type),
      editable: false,
      render: (v: string, r: StockHistory) => {
        const getDisplayName = (type: string) => {
          switch (type) {
            case "sale":
              return "Sale";
            case "product":
              return "Product";
            case "service":
              return "Service";
            case "adjustment":
              return "Adjustment";
            case "opening":
              return "Opening";
            default:
              return type;
          }
        };
        
        return (
          <Tag color={getRefTypeColor(r.ref_type)}>
            {getDisplayName(r.ref_type)}
          </Tag>
        );
      },
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>Inventory History</Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder="Search stock or product names..."
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form component={false}>
          <Table
            showHeader={data.stockHistories.length > 0}
            dataSource={data.stockHistories.map((b) => ({ key: b.id, ...b }))}
            columns={columns}
            rowClassName="editable-row"
            onChange={(p: PaginationConfig) => {
              setPagination(p);
            }}
            pagination={{
              total: totalCount.data?.aggregateStockHistory._count.id || 0,
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            scroll={{ x: 800 }}
          />
        </Form>
      </Col>
    </Row>
  );

  const filters = (
    <Row style={{ marginTop: 4, marginRight: 2 }}>
      <Col span={24}>
        <MultiSelect
          selected={transactionTypeFilter}
          placeholder="Select reference types"
          options={["sale", "product", "service", "adjustment", "opening"]}
          onChange={setTransactionTypeFilter}
        />
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      {context}
    </React.Fragment>
  );
};

export default withClinic(InventoryHistoryPage);
